import React from 'react';
import { Page, Text, View, Document, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    padding: 20,
    backgroundColor: 'white'
  },
  header: {
    marginBottom: 15,
    textAlign: 'center'
  },
  title: {
    fontSize: 14,
    marginBottom: 8
  },
  section: {
    marginBottom: 8
  },
  row: {
    flexDirection: 'row',
    paddingVertical: 3,
    borderBottomWidth: 0.5,
    borderBottomColor: '#eee'
  },
  headerRow: {
    flexDirection: 'row',
    paddingVertical: 3,
    backgroundColor: '#f5f5f5',
    marginBottom: 3
  },
  regionRow: {
    flexDirection: 'row',
    paddingVertical: 3,
    backgroundColor: '#fafafa'
  },
  totalRow: {
    flexDirection: 'row',
    paddingVertical: 3,
    backgroundColor: '#fff3cd',
    marginTop: 3
  },
  col1: {
    flex: 2,
    paddingRight: 8
  },
  col2: {
    flex: 1,
    textAlign: 'right'
  },
  col3: {
    flex: 1,
    textAlign: 'right'
  },
  text: {
    fontSize: 8
  },
  bold: {
    fontWeight: 'bold'
  },
  disclaimer: {
    fontSize: 6,
    marginTop: 15,
    color: 'grey'
  }
});

interface Territory {
  ask: number;
  take: number;
}

interface PDFDocumentProps {
  title: string;
  director: string;
  territories: {
    [key: string]: {
      [key: string]: Territory;
    };
  };
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(value);
};

const capitalizeRegion = (region: string) => {
  return region
    .split(/(?=[A-Z])/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const PDFDocument: React.FC<PDFDocumentProps> = ({ title, director, territories }) => {
  const calculateRegionTotal = (region: { [key: string]: Territory }) => {
    return Object.values(region).reduce(
      (acc, territory) => ({
        ask: acc.ask + territory.ask,
        take: acc.take + territory.take
      }),
      { ask: 0, take: 0 }
    );
  };

  const calculateGrandTotal = () => {
    return Object.values(territories).reduce(
      (acc, region) => {
        const regionTotal = calculateRegionTotal(region);
        return {
          ask: acc.ask + regionTotal.ask,
          take: acc.take + regionTotal.take
        };
      },
      { ask: 0, take: 0 }
    );
  };

  const totals = calculateGrandTotal();

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Text style={[styles.title, styles.bold]}>CINELAUNCH INTERNATIONAL SALES ESTIMATES</Text>
          <Text style={styles.text}>Title: {title || 'Untitled Project'}</Text>
          <Text style={styles.text}>Director: {director || 'TBD'}</Text>
        </View>

        <View style={styles.section}>
          <View style={styles.headerRow}>
            <Text style={[styles.text, styles.col1, styles.bold]}>Territory</Text>
            <Text style={[styles.text, styles.col2, styles.bold]}>Asks</Text>
            <Text style={[styles.text, styles.col3, styles.bold]}>Takes</Text>
          </View>

          {Object.entries(territories).map(([regionName, region]) => (
            <React.Fragment key={regionName}>
              <View style={styles.regionRow}>
                <Text style={[styles.text, styles.col1, styles.bold]}>
                  {capitalizeRegion(regionName)}
                </Text>
                <Text style={[styles.text, styles.col2]}>
                  {formatCurrency(calculateRegionTotal(region).ask)}
                </Text>
                <Text style={[styles.text, styles.col3]}>
                  {formatCurrency(calculateRegionTotal(region).take)}
                </Text>
              </View>
              {Object.entries(region).map(([territory, values]) => (
                <View key={territory} style={styles.row}>
                  <Text style={[styles.text, styles.col1, { paddingLeft: 8 }]}>
                    {territory}
                  </Text>
                  <Text style={[styles.text, styles.col2]}>
                    {formatCurrency(values.ask)}
                  </Text>
                  <Text style={[styles.text, styles.col3]}>
                    {formatCurrency(values.take)}
                  </Text>
                </View>
              ))}
            </React.Fragment>
          ))}

          <View style={styles.totalRow}>
            <Text style={[styles.text, styles.col1, styles.bold]}>Total MG Estimate</Text>
            <Text style={[styles.text, styles.col2, styles.bold]}>{formatCurrency(totals.ask)}</Text>
            <Text style={[styles.text, styles.col3, styles.bold]}>{formatCurrency(totals.take)}</Text>
          </View>
        </View>

        <Text style={styles.disclaimer}>
          The foregoing projections are estimates only and include estimates expected. No representation is being made that these estimates will be realized. 
          Actual results may vary materially from the projections for numerous reasons including, but not limited to, prevailing market conditions, territorial 
          and global economic climate, the final cast and creative elements of the Picture and U.S. theatrical release. Consequently, no guarantee is presented 
          or implied as to the accuracy of specific projections or predictive statements contained herein. The foregoing projections are strictly confidential and, 
          without the prior written consent of Cinelaunch, may not be disclosed by the party to whom they are furnished to any other person.
        </Text>
      </Page>
    </Document>
  );
};

export default PDFDocument;